import { ImagePaths } from '../../lib/imageUrls.js';

export const smbEssentialEightStarter = {
  id: 35,
  slug: 'smb-essential-eight-starter',
  ogImage: '/og/courses/smb-essential-eight-starter.jpg',
  courseCode: 'SMB-E8-START-001',
  featured: false,
  title: 'Essential Eight Starter for Australian Small Business',
  subtitle: 'Plain-English cyber security uplift for teams of 5 to 50',
  valueProposition: 'A practical, owner-friendly programme that helps Australian small businesses reach Essential Eight Maturity Level 1, produce evidence cyber insurers and ACSC want to see, and stop the most common attacks — without hiring a security team.',
  duration: '6 hours',
  students: 'New',
  level: 'Foundational',
  image: ImagePaths.COURSE_2,
  shortDescription: 'SMB-E8-START-001 — Plain-English Essential Eight starter covering MFA, patching, backups, and insurance evidence for Australian SMB owners targeting Maturity Level 1.',
  price: {
    aud: 499,
    currency: 'AUD',
    earlyBird: 399
  },
  stripeConfig: {
    productId: 'prod_UarsLMO3DvNbav',
    priceId: 'price_1TikaKKu1nyc322U4A7W9Ygm'
  },

  snapshot: {
    whoItsFor: [
      'Small business owners wearing the cyber security hat',
      'IT generalists supporting 5–50 employee teams',
      'Office managers responsible for risk, insurance and compliance',
      'Bookkeepers and operations leads handling sensitive client data'
    ],
    accreditation: 'Aligned to ASD Essential Eight Maturity Level 1 and the ACSC Small Business Cyber Security Guide'
  },

  logistics: {
    classSize: '3 × 2-hour live evening sessions with downloadable templates',
    requirements: [
      'A laptop with admin rights to your business email and devices',
      'Access to your current Microsoft 365 or Google Workspace tenant',
      'A rough list of business-critical apps and where backups live',
      'No prior cyber security experience required'
    ]
  },

  overview: 'This Essential Eight Starter is built for the realities of running a small Australian business. Across three evening sessions you will learn what the Essential Eight actually is, why the Australian Signals Directorate and Australian Cyber Security Centre treat it as the baseline, and how to reach Maturity Level 1 with the tools you already pay for. Every module references the ACSC Small Business Cyber Security Guide, business.gov.au cyber pages and the federally backed Cyber Wardens programme so the steps you take are defensible to insurers, auditors and your board.\n\nThe focus is owner-friendly: no jargon, no enterprise-only tooling, and every activity produces an artefact you can keep — a policy, a checklist, a screenshot or a register entry. By the end you will have an Essential Eight Maturity Level 1 plan, evidence for cyber insurance renewals, and a simple incident reporting workflow into ReportCyber.',

  outcomes: [
    'Explain the eight mitigation strategies and Maturity Level 1 in plain English to your team',
    'Turn on multi-factor authentication across email, banking, accounting and admin accounts',
    'Set a sensible password and passphrase standard that survives an insurance questionnaire',
    'Build a lightweight patching routine for Windows, macOS, browsers and Microsoft Office',
    'Run and test a 3-2-1 backup that actually restores when ransomware hits',
    'Apply application control on a budget using Microsoft Defender, Smart App Control or AppLocker',
    'Assemble an evidence pack for cyber insurance renewals and ACSC ReportCyber notifications',
    'Map your current state against Essential Eight Maturity Level 1 and produce a 90-day uplift plan'
  ],

  audience: [
    'Small business owners and directors carrying the cyber security responsibility themselves',
    'Office managers handling risk, insurance renewals and client compliance questionnaires',
    'IT generalists and one-person IT teams supporting 5 to 50 staff',
    'Bookkeepers, accountants and operations leads handling sensitive client data',
    'Trades, allied health and professional services businesses bidding for government work',
    'Franchise and not-for-profit managers who need a defensible baseline on a small budget'
  ],

  prerequisites: [
    'No prior cyber security experience required',
    'A laptop with administrator rights to your business email and devices',
    'Access to your current Microsoft 365 or Google Workspace tenant',
    'A rough list of your business-critical apps and where your backups currently live',
    'Willingness to make configuration changes to your own environment during the sessions'
  ],

  modules: [
    {
      number: 1,
      title: 'What the Essential Eight is and why it matters',
      duration: '60 min',
      topics: [
        'Origins of the Essential Eight and the ASD/ACSC role',
        'Maturity Level 0, 1, 2, 3 explained for a small business',
        'How insurers and government buyers use the Essential Eight',
        'Reading the ACSC Small Business Cyber Security Guide',
        'Setting a realistic Maturity Level 1 target for your business'
      ]
    },
    {
      number: 2,
      title: 'MFA and password basics that survive an audit',
      duration: '75 min',
      topics: [
        'Turning on MFA in Microsoft 365, Google Workspace, Xero and MYOB',
        'Authenticator apps, passkeys and phishing-resistant options',
        'Australian Cyber Security Centre passphrase guidance in practice',
        'Password manager rollout for a small team',
        'Evidence to capture: screenshots, exports and policy statements'
      ]
    },
    {
      number: 3,
      title: 'Patching and backups for tiny teams',
      duration: '75 min',
      topics: [
        'A 48-hour patching rhythm for operating systems and browsers',
        'Microsoft Office, PDF readers and add-ins — the riskiest gaps',
        'The 3-2-1 backup rule using OneDrive, Google Drive and an offline copy',
        'Restoring a file and a device — a test you run, not a hope',
        'Backup register template and quarterly restore drill'
      ]
    },
    {
      number: 4,
      title: 'Application control on a budget',
      duration: '75 min',
      topics: [
        'What application control means at Maturity Level 1',
        'Microsoft Defender Smart App Control and SmartScreen settings',
        'Using AppLocker or Windows Defender Application Control on Pro/Business SKUs',
        'macOS Gatekeeper and notarisation checks',
        'Hardening Microsoft Office macros and blocking risky file types'
      ]
    },
    {
      number: 5,
      title: 'Building evidence for cyber insurance and ACSC reporting',
      duration: '75 min',
      topics: [
        'What cyber insurers actually ask for at renewal',
        'Assembling a one-page Essential Eight evidence pack',
        'Reporting incidents to ACSC via ReportCyber and to the OAIC under the Notifiable Data Breaches scheme',
        'Cyber Wardens programme — using free federal training to upskill staff',
        'Your 90-day Maturity Level 1 uplift plan'
      ]
    }
  ],

  format: {
    type: 'Three live online evening sessions with downloadable templates',
    includes: [
      '6 hours of plain-English instruction across three 2-hour evening sessions',
      'Step-by-step walkthroughs in Microsoft 365, Google Workspace, Xero and MYOB',
      'MFA, patching and backup checklists you complete on your own environment',
      'Backup register template and quarterly restore drill procedure',
      'Macro-hardening and application control checklists for Windows and macOS',
      'One-page Essential Eight evidence pack template for cyber insurance renewals',
      'Essential Eight Maturity Level 1 self-assessment and a 90-day uplift plan',
      'Certificate of completion listing CPD hours'
    ]
  },

  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Three consecutive Tuesday evenings, 6:00pm to 8:00pm AEST, so no business hours are lost.'
  },

  faqs: [
    {
      question: 'Do I need an IT background to take this course?',
      answer: 'No. The course is written for owners, office managers and IT generalists. Every step is demonstrated in Microsoft 365, Google Workspace and common SMB tools, with checklists you can follow without prior cyber security experience.'
    },
    {
      question: 'Will this get my business to Essential Eight Maturity Level 1?',
      answer: 'Yes — that is the target. You finish with a Maturity Level 1 self-assessment, a 90-day uplift plan and an evidence pack aligned to the ACSC Small Business Cyber Security Guide (updated 2025) so you can demonstrate progress to insurers and clients.'
    },
    {
      question: 'How is this different from the Essential Eight Fundamentals course?',
      answer: 'Fundamentals is an 8-hour deep dive across all three maturity levels for IT and security staff. The Starter is a focused 6-hour programme for non-technical SMB leaders aiming squarely at Maturity Level 1 with budget-conscious tooling.'
    },
    {
      question: 'Does the course reference official Australian sources?',
      answer: 'Every module cites the ACSC Small Business Cyber Security Guide, business.gov.au cyber security pages and the Cyber Wardens programme content, all with publication dates so you can verify the guidance you are following.'
    },
    {
      question: 'What evidence will I have at the end of the course?',
      answer: 'A Maturity Level 1 self-assessment, MFA and patching screenshots, a backup register, a restore test log, a macro-hardening checklist and a one-page cyber insurance evidence pack — enough to support a renewal conversation or an ACSC ReportCyber follow-up.'
    },
    {
      question: 'Can I claim this for professional development?',
      answer: 'Yes. The six contact hours qualify for CPD with most Australian professional bodies, and the certificate of completion includes the hours and learning outcomes you need to log the claim.'
    }
  ],

  australianContext: true,
  complianceFrameworks: [
    'ASD Essential Eight (Maturity Level 1 target)',
    'ACSC Small Business Cyber Security Guide'
  ]
};
